import { useRouter } from 'next/router';
import { useAchievementTypes, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  AchievementIcon,
  AchievementUserStatus
} from 'components/achievements';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  EmptyIndicator,
  FlexContainer,
  LinkButton,
  Owners,
  Pagination,
  TitleWithIcon
} from 'components/common';
import { ProfileAchievementsHeader } from '.';

import type { GetUserAchievementsRes } from 'shared-types/responses';

interface Props {
  isUserPage?: boolean;
}

const ProfileAchievements = ({ isUserPage = false }: Props) => {
  const {
    messages,
    pageState: { user },
    serverConfig
  } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const router = useRouter();
  const queryParams = Object.keys(router.query)
    .map((key) => `${key}=${router.query[key]}`)
    .join('&');

  const { data, loading } = useApiData<GetUserAchievementsRes>({
    url:
      `/admin/users/${user.id}/achievements` +
      (queryParams ? `?${queryParams}` : ''),
    onlyIf: Boolean(user)
  });

  return (
    <div className="achievements">
      <Box
        headerContent={<ProfileAchievementsHeader />}
        loading={loading}
        padding={0}
        title={messages.commonAchievementsAlt}
      >
        {data?.achievements?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell>{messages.commonTitle}</BoxTableCell>
                <BoxTableCell>{messages.commonType}</BoxTableCell>
                <BoxTableCell>{messages.commonStatus}</BoxTableCell>
              </BoxTableHeader>

              {data.achievements.map((achievement) => {
                const { id, type, title, numberOfItems, owners } = achievement;

                const href = isUserPage
                  ? `/users/${user?.id}/achievements/${id}`
                  : `/achievements/${id}`;

                const validCompletions = achievement.completions.filter(
                  (completion) => !completion.isInactive
                );

                const numberOfCompletions = validCompletions.length
                  ? validCompletions.length
                  : achievement.numberOfPreviousCompletions || 0;

                return (
                  <BoxTableRow key={id}>
                    <BoxTableCell>
                      <FlexContainer style={{ gap: 15, minWidth: 250 }}>
                        <AchievementIcon size="small" type={type} />

                        <TitleWithIcon title={title} titleLink={{ href }}>
                          {serverConfig.productHideOwner ? null : (
                            <Owners owners={owners} />
                          )}
                        </TitleWithIcon>
                      </FlexContainer>
                    </BoxTableCell>

                    <BoxTableCell>{achievementTypes[type].title}</BoxTableCell>

                    <BoxTableCell>
                      <FlexContainer style={{ gap: 5 }}>
                        <AchievementUserStatus
                          achievementData={{ ...achievement.completions[0] }}
                          achievementType={type}
                          numberOfCompletions={numberOfCompletions}
                          numberOfItems={numberOfItems}
                        />

                        {achievement.numberOfPreviousCompletions ? (
                          <LinkButton
                            category="lighter"
                            href={href}
                            size="header"
                            style={{
                              fontSize: '1.3rem',
                              padding: '4px 6px 3px'
                            }}
                            text={`+${achievement.numberOfPreviousCompletions}`}
                            tooltip={messages.completionsPreviousCompletions}
                          />
                        ) : null}
                      </FlexContainer>
                    </BoxTableCell>
                  </BoxTableRow>
                );
              })}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator
            icon="check-circle"
            title={messages.achievementsEmpty}
          />
        )}
      </Box>

      <Pagination
        currentPage={parseInt((router.query.page as string) || '1')}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => {
          router.replace({ query: { ...router.query, page } }, undefined, {
            shallow: true
          });
        }}
      />
    </div>
  );
};

export default ProfileAchievements;
